import React, { useState, useEffect } from "react";

import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";

import { useSelector, useDispatch } from "react-redux";
import {
  selectItemsPerPage,
  setItemsPerPage,
  selectHomeDefaultSort,
  selectAllEntryDefaultSort,
  selectEntryDefaultSort,
  selectPatientDefaultSort,
  selectEntryThumbnailDefaultSort,
} from "../../redux/features/applicationState/applicationStateSlice";

// DataTable Components
import { SortingButtons } from "./components/SortingButtons";
import {
  homeRowFilter,
  entryRowFilter,
  patientRowFilter,
  entryThumbnailRowFilter,
} from "./components/rowFilters";
import {
  EntryRow,
  PatientRow,
  EntryThumbnailRow,
} from "./components/RowComponents";
import { SquareIconNavButton } from "../Buttons/Buttons";

const DataTable = ({
  data,
  dataTags,
  rowType,
  showFilter = true,
  showPageNav = true,
  additionalArgs = {},
}) => {
  // Items per page
  const dispatch = useDispatch();
  const itemsPerPage = useSelector(selectItemsPerPage);

  const sortingButtonsDefaultSort = {
    home: useSelector(selectHomeDefaultSort),
    allEntry: useSelector(selectAllEntryDefaultSort),
    entry: useSelector(selectEntryDefaultSort),
    patient: useSelector(selectPatientDefaultSort),
    entryThumbnail: useSelector(selectEntryThumbnailDefaultSort),
  }[rowType];
  const sortingButtonsCategoryMapping = {
    home: {
      dateModified: "date_modified",
      appointmentDate: "appointment_date",
      entryType: "entry_type",
      uniqueId: "unique_id",
      patientName: "patient_owner_full_name",
    },
    allEntry: {
      dateModified: "date_modified",
      appointmentDate: "appointment_date",
      entryType: "entry_type",
    },
    entry: {
      dateModified: "date_modified",
      appointmentDate: "appointment_date",
    },
    patient: {
      dateModified: "date_modified",
      lastAppointmentDate: "last_appointment_date",
      uniqueId: "unique_id",
      patientName: "full_name",
    },
    entryThumbnail: {
      dateModified: "date_modified",
      appointmentDate: "appointment_date",
      entryType: "entry_type",
    },
  }[rowType];
  const sortingButtonsCategories = {
    home: [
      { label: "Date Modified", by: "dateModified" },
      { label: "Appointment Date", by: "appointmentDate" },
      { label: "Entry Type", by: "entryType" },
      { label: "Unique Patient ID", by: "uniqueId" },
      { label: "Patient Name", by: "patientName" },
    ],
    allEntry: [
      { label: "Date Modified", by: "dateModified" },
      { label: "Appointment Date", by: "appointmentDate" },
      { label: "Entry Type", by: "entryType" },
    ],
    entry: [
      { label: "Date Modified", by: "dateModified" },
      { label: "Appointment Date", by: "appointmentDate" },
    ],
    patient: [
      { label: "Date Modified", by: "dateModified" },
      { label: "Last Appointment Date", by: "lastAppointmentDate" },
      { label: "Unique Patient ID", by: "uniqueId" },
      { label: "Patient Name", by: "patientName" },
    ],
    entryThumbnail: [
      { label: "Date Modified", by: "dateModified" },
      { label: "Appointment Date", by: "appointmentDate" },
      { label: "Entry Type", by: "entryType" },
    ],
  }[rowType];

  const filterFunctions = {
    home: homeRowFilter,
    allEntry: entryRowFilter,
    entry: entryRowFilter,
    patient: patientRowFilter,
    entryThumbnail: entryThumbnailRowFilter,
  };

  const RowComponent = {
    home: EntryRow,
    allEntry: EntryRow,
    entry: EntryRow,
    patient: PatientRow,
    entryThumbnail: EntryThumbnailRow,
  }[rowType];

  const filterFunction = filterFunctions[rowType];

  const tagsAccessorMapping = {
    home: (row) => row?.classification_tags,
    allEntry: (row) => row?.classification_tags,
    entry: (row) => row?.classification_tags,
    patient: (row) => row?.classification_tags,
    entryThumbnail: (row) => row?.classification_tags,
  };

  const [filterSearchOpen, setFilterSearchOpen] = useState(true);
  const [tagsOpen, setTagsOpen] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const [filteredData, setFilteredData] = useState([]);

  const [selectedTags, setSelectedTags] = useState([]);

  // Debounce searchTerm. Only perform search when user stops typing.
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.toLowerCase());
    }, 350);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  useEffect(() => {
    // Filter the data using the debouncedSearchTerm
    const selectedTagsSet = new Set(selectedTags);
    const newFilteredData = data.filter((row) =>
      filterFunction(row, debouncedSearchTerm, selectedTagsSet)
    );
    setFilteredData(newFilteredData);
  }, [data, debouncedSearchTerm, selectedTags]);

  // Reset the current page if any filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, selectedTags]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  // Get what items to display on page
  const getDisplayData = () => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return filteredData.slice(start, end);
  };

  // Handle tag toggling
  const handleItemClick = (item, setItems) => {
    setItems((currentItems) => {
      if (currentItems.includes(item)) {
        return currentItems.filter((i) => i !== item);
      }
      return [...currentItems, item];
    });
  };

  // Count how many rows match each tag type
  function countRowsPerTag(rowData, propValues) {
    const counts = {};

    // Initialize counts for all tags to 0
    propValues.forEach((tag) => {
      counts[tag] = 0;
    });

    const accessorFunction = tagsAccessorMapping[rowType];

    // Loop over the array of row data
    rowData.forEach((row) => {
      const rowTags = accessorFunction ? accessorFunction(row) : null;
      if (rowTags) {
        // Check if tags exist for the row
        let parsedTags = [];
        try {
          if (Array.isArray(rowTags)) {
            parsedTags = rowTags;
          }
        } catch (e) {
          console.error("Error parsing tags for row:", row, e);
        }

        if (Array.isArray(parsedTags)) {
          // Ensure parsed data is an array
          parsedTags.forEach((tag) => {
            if (counts[tag] !== undefined) {
              counts[tag]++;
            }
          });
        }
      }
    });

    return counts;
  }

  const rowCountsTags = countRowsPerTag(data, dataTags);

  return (
    // Justify-between to keep Filter and Content at top, and Page Nav at bottom of any container.
    <div className="flex flex-col items-center justify-between flex-grow w-full Xbg-red-500">
      {/* ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- */}
      <div className="flex flex-col items-center justify-center w-full Xbg-blue-500">
        {/* Search and filter options */}
        {showFilter && (
          <div className="flex flex-col items-start justify-start w-full pb-5">
            {/* Toggle showing search and filter options */}

            <button
              className="flex flex-row items-center justify-end mb-4
                     text-neutral-700 hover:text-neutral-600 active:text-neutral-900"
              onClick={() => setFilterSearchOpen(!filterSearchOpen)}
            >
              <p className="text-lg font-bold">Filter and Search</p>
              <MagnifyingGlassIcon
                className={"ml-2 h-5 w-5 flex-none"}
                aria-hidden="true"
              />
            </button>

            {/* Container which hides / shows search and filter options */}
            {filterSearchOpen && (
              <>
                <div
                  className="flex justify-between w-full Xpx-4
                           flex-col md:flex-row
                           items-end md:items-center
                          "
                >
                  {/* General search bar */}
                  <input
                    className="flex items-center justify-start py-4 px-2
                             border border-neutral-300 rounded-lg
                             focus:outline-none focus:ring-inset focus:ring-neutral-200
                             w-full Xmd:w-1/2 Xxl:w-1/3
                            "
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search..."
                  />
                </div>

                {/* ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- */}
                <div className="flex flex-row items-center justify-center w-full pt-3 pb-2 Xbg-red-500">
                  <SortingButtons
                    rowType={rowType}
                    data={filteredData}
                    setData={setFilteredData}
                    defaultSort={sortingButtonsDefaultSort}
                    categoryMapping={sortingButtonsCategoryMapping}
                    categories={sortingButtonsCategories}
                  />
                </div>
                {/* ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- */}

                {/* Toggle content tags sort */}
                <button
                  className="flex flex-row items-center justify-end Xml-4 mt-4 Xpx-2 py-1 font-bold
                       text-neutral-700 hover:text-neutral-600 active:text-neutral-900"
                  onClick={() => setTagsOpen(!tagsOpen)}
                >
                  Tags
                  <ChevronDownIcon
                    className={`ml-2 h-5 w-5 flex-none transition-all duration-200 ${
                      tagsOpen && "rotate-180"
                    }`}
                    aria-hidden="true"
                  />
                </button>

                {/* Tag category buttons */}
                {tagsOpen && (
                  <div className="flex flex-wrap items-center justify-start w-full py-2">
                    {dataTags
                      .filter((tag) => tag.includes(debouncedSearchTerm))
                      .map((tag, index) => (
                        <button
                          key={`${tag}-${index}`}
                          onClick={() => handleItemClick(tag, setSelectedTags)}
                          className={`mr-2 my-1 rounded-lg px-3 py-1 text-sm font-semibold text-neutral-700 ${
                            selectedTags.includes(tag)
                              ? "bg-green-200 hover:bg-green-300 active:bg-green-400"
                              : "bg-neutral-200 hover:bg-neutral-300 active:bg-neutral-400"
                          }`}
                        >
                          {tag} ({rowCountsTags[tag]})
                        </button>
                      ))}
                  </div>
                )}

                {/* Items per page info */}
                <div className="flex flex-col md:flex-row items-start md:justify-between pt-2 space-y-4 md:space-y-0 w-full Xbg-blue-500">
                  {/* Number of results */}
                  <p className="flex items-center justify-center text-neutral-700 h-10 Xbg-orange-500">
                    Showing results {(currentPage - 1) * itemsPerPage + 1} –{" "}
                    {Math.min(currentPage * itemsPerPage, filteredData.length)}{" "}
                    out of {filteredData.length} matches
                  </p>

                  {/* Items per page dropdown */}
                  <div className="flex flex-row items-center justify-between h-10 w-full md:w-auto Xbg-red-500">
                    <p className="text-neutral-700">Items per page:</p>
                    <select
                      className="flex flex-row items-center justify-end w-12 ml-2 py-1 px-2 bg-white border border-neutral-300 rounded-lg
                                 focus:outline-none focus:ring-inset focus:ring-neutral-200
                                 appearance-none"
                      value={itemsPerPage}
                      onChange={(e) => {
                        dispatch(setItemsPerPage(e.target.value));
                      }}
                    >
                      <option disabled value="">
                        {"Items per page"}
                      </option>
                      {[5, 10, 20, 50, 100].map((option, index) => (
                        <option key={`${index}`} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- */}

        {/* Data contents */}
        <div className="w-full grid grid-cols-1 gap-4">
          {getDisplayData().map((item, index) => (
            <RowComponent
              key={`${index}-${item?.id ? item.id : index}`}
              row={item}
              additionalArgs={additionalArgs}
            />
          ))}
        </div>
      </div>
      {/* ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- */}

      {/* Page navigation */}
      {showPageNav && (
        <div className="flex flex-row items-center justify-between w-full pt-5">
          {/* Current visible items out of total matching items */}
          <p className="text-neutral-700">
            {(currentPage - 1) * itemsPerPage + 1} –{" "}
            {Math.min(currentPage * itemsPerPage, filteredData.length)} of{" "}
            {filteredData.length}
          </p>

          {/* Current page */}
          <p className="text-neutral-700">
            Page {currentPage} of {totalPages}
          </p>

          {/* Navigation Buttons */}
          <div className="flex flex-row space-x-2 md:space-x-3 lg:space-x-4">
            <SquareIconNavButton
              Icon={ChevronLeftIcon}
              actionFunction={() => setCurrentPage((prevPage) => prevPage - 1)}
              activeColor={"active:bg-cyan-100"}
              disabled={currentPage === 1}
            />

            <SquareIconNavButton
              Icon={ChevronRightIcon}
              actionFunction={() => setCurrentPage((prevPage) => prevPage + 1)}
              activeColor={"active:bg-cyan-100"}
              disabled={currentPage === totalPages}
            />
          </div>
        </div>
      )}

      {/* ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- ----- */}
    </div>
  );
};

export default DataTable;
